import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import QuickCreateSheet from './QuickCreateSheet';
import { userManagementApi } from '../api/userManagement';
import * as contentStudioApi from '../api/contentStudioV2';

const BottomNavBar = () => {
  const { user, token, isSupervisor, isManagement, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showQuickCreate, setShowQuickCreate] = useState(false);
  const [queueCount, setQueueCount] = useState(0);
  const [videoCount, setVideoCount] = useState(0);

  // Fetch queue counts for supervisors
  useEffect(() => {
    const fetchQueueCounts = async () => {
      if (!token || (!isSupervisor() && !isManagement() && !isAdmin())) return;
      
      try {
        const [queueRes, statsRes] = await Promise.all([
          userManagementApi.getApprovalQueue(),
          contentStudioApi.getSupervisorStats(token)
        ]);
        
        setQueueCount(queueRes?.queue?.length || queueRes?.items?.length || 0);
        setVideoCount(statsRes?.summary?.videos_pending || 0);
      } catch (error) {
        console.error('Failed to fetch queue counts:', error);
      }
    };

    fetchQueueCounts();
    // Refresh every 30 seconds
    const interval = setInterval(fetchQueueCounts, 30000);
    return () => clearInterval(interval);
  }, [token, isSupervisor, isManagement, isAdmin]);

  const totalPending = queueCount + videoCount;

  const tabs = [
    {
      key: 'home',
      label: 'Home',
      labelBM: 'Utama',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
          <polyline points="9,22 9,12 15,12 15,22"/>
        </svg>
      ),
      route: '/dashboard',
      roles: ['all'],
      ariaLabel: 'Pergi ke Halaman Utama'
    },
    {
      key: 'studios',
      label: 'Studios',
      labelBM: 'Studios',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="3" width="7" height="7"/>
          <rect x="14" y="3" width="7" height="7"/>
          <rect x="14" y="14" width="7" height="7"/>
          <rect x="3" y="14" width="7" height="7"/>
        </svg>
      ),
      route: '/studios',
      roles: ['all'],
      ariaLabel: 'Buka Studios'
    },
    {
      key: 'create',
      label: '',
      labelBM: '',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <line x1="12" y1="5" x2="12" y2="19"/>
          <line x1="5" y1="12" x2="19" y2="12"/>
        </svg>
      ),
      route: null,
      roles: ['all'],
      ariaLabel: 'Tambah Baharu',
      isCenter: true
    },
    {
      key: 'mywork',
      label: 'My Work',
      labelBM: 'Karya',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
          <polyline points="14,2 14,8 20,8"/>
          <line x1="16" y1="13" x2="8" y2="13"/>
          <line x1="16" y1="17" x2="8" y2="17"/>
          <polyline points="10,9 9,9 8,9"/>
        </svg>
      ),
      route: '/my-submissions',
      roles: ['executor'],
      ariaLabel: 'Lihat Karya Saya'
    },
    {
      key: 'queue',
      label: 'Queue',
      labelBM: 'Kelulusan',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/>
          <rect x="8" y="2" width="8" height="4" rx="1" ry="1"/>
        </svg>
      ),
      route: '/queue',
      roles: ['supervisor', 'management', 'admin'],
      ariaLabel: 'Lihat Queue Kelulusan'
    },
    {
      key: 'profile',
      label: 'Profile',
      labelBM: 'Profil',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
          <circle cx="12" cy="7" r="4"/>
        </svg>
      ),
      route: '/profile',
      roles: ['all'],
      ariaLabel: 'Buka Profil'
    }
  ];

  // Add Analytics tab for Management+ roles
  if (isManagement() || isAdmin()) {
    tabs.splice(4, 0, {
      key: 'analytics',
      label: 'Analytics',
      labelBM: 'Analitik',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="18" y1="20" x2="18" y2="10"/>
          <line x1="12" y1="20" x2="12" y2="4"/>
          <line x1="6" y1="20" x2="6" y2="14"/>
        </svg>
      ),
      route: '/analytics',
      roles: ['management', 'admin'],
      ariaLabel: 'Buka Analytics'
    });
  }

  const handleTabPress = (tab) => {
    if (tab.key === 'create') {
      setShowQuickCreate(true);
      if (navigator.vibrate) navigator.vibrate(10);
    } else if (tab.route) {
      navigate(tab.route);
      if (navigator.vibrate) navigator.vibrate(10);
    }
  };

  const isTabVisible = (tab) => {
    if (tab.roles.includes('all')) return true;
    
    const userRole = user?.role;
    if (!userRole) return false;
    
    // Check for analytics tab specifically
    if (tab.key === 'analytics') {
      return isManagement() || isAdmin();
    }

    // Hide Queue tab for management users - they use Analytics Hub instead
    if (tab.key === 'queue') {
      // Only show Queue for supervisors, NOT for management
      return isSupervisor() && !isManagement() && !isAdmin();
    }

    // Check for mywork tab - only for executors (not supervisors/managers)
    if (tab.key === 'mywork') {
      return userRole === 'executor' && !isSupervisor() && !isManagement() && !isAdmin();
    }
    
    if (tab.roles.includes('supervisor') && (isSupervisor() || isManagement() || isAdmin())) return true;
    if (tab.roles.includes('management') && (isManagement() || isAdmin())) return true;
    if (tab.roles.includes('admin') && isAdmin()) return true;
    
    return tab.roles.includes(userRole);
  };

  const isActiveTab = (tab) => {
    if (!tab.route) return false;
    return location.pathname === tab.route || 
           (tab.route === '/dashboard' && location.pathname === '/');
  };

  const visibleTabs = tabs.filter(isTabVisible);

  return (
    <>
      {/* Compact Navigation Bar - 56px + Safe Area */}
      <div 
        className="bg-white border-t border-gray-100 h-full shadow-2xl" 
        style={{
          boxShadow: '0 -4px 16px rgba(0, 0, 0, 0.1), 0 -2px 8px rgba(0, 0, 0, 0.06)',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)'
        }}
      >
        <div 
          className="flex items-center justify-around px-2" 
          style={{ 
            height: '56px'
          }}
        >
          {visibleTabs.map((tab) => (
            <Button
              key={tab.key}
              onMouseDown={(e) => {
                e.preventDefault();
                handleTabPress(tab);
              }}
              onClick={() => handleTabPress(tab)}
              variant="ghost"
              className={`
                flex flex-col items-center justify-center relative
                min-w-[56px] min-h-[48px] px-2 py-1 touch-target
                transition-all duration-200 rounded-xl
                hover:bg-blue-50 active:bg-blue-100
                ${tab.isCenter ? 'relative' : ''}
                ${isActiveTab(tab) ? 'text-emzi-blue bg-blue-50' : 'text-gray-600'}
                ${tab.isCenter ? 'bg-emzi-blue text-white rounded-full w-12 h-12 shadow-lg hover:bg-blue-600 hover:shadow-xl' : ''}
                ${tab.isCenter ? 'transform hover:scale-105 active:scale-95' : ''}
              `}
              aria-label={tab.ariaLabel}
              data-testid={`nav-tab-${tab.key}`}
            >
              {/* Icon */}
              <div className={`${tab.isCenter ? '' : 'mb-0.5'} relative`}>
                {tab.icon}
                
                {/* Notification Badge for Queue tab */}
                {tab.key === 'queue' && totalPending > 0 && (
                  <span className="absolute -top-2 -right-2 bg-red-500 text-white text-[10px] font-bold min-w-[18px] h-[18px] rounded-full flex items-center justify-center px-1 shadow-md animate-pulse">
                    {totalPending > 99 ? '99+' : totalPending}
                  </span>
                )}
              </div>
              
              {/* Label (hidden for center button) */}
              {!tab.isCenter && (
                <span className="text-[10px] font-medium leading-tight">
                  {tab.label}
                </span>
              )}
              
              {/* Active indicator */}
              {isActiveTab(tab) && !tab.isCenter && (
                <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-6 h-0.5 bg-emzi-blue rounded-full"></div>
              )}
            </Button>
          ))}
        </div>
      </div>

      {/* Quick Create Sheet */}
      <QuickCreateSheet 
        isOpen={showQuickCreate}
        onClose={() => setShowQuickCreate(false)}
      />
    </>
  );
};

export default BottomNavBar;